import { type FC, type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useOverlay } from "../../hooks/useOverlay";
import { Button } from "./Button";
import { OverlayFooter } from "./OverlayFooter";

export interface SideSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  widthClass?: string;
  /** Replaces the default title/subtitle header block when provided. Close button still renders. */
  headerContent?: ReactNode;
  /** Extra classes for the header container. Overrides default padding/bg when custom content needs it. */
  headerClassName?: string;
  /** Hide the default Close button (e.g. when headerContent renders its own). */
  hideCloseButton?: boolean;
  /** Override the default "Close" button label (e.g. "‹ Back to Daedalus" for
   *  an overlay sheet whose close returns to the base). */
  closeLabel?: string;
  /** Z-index tailwind class. Default "z-50"; pass "z-[60]" for a sheet
   *  Stacked over another so the overlay paints above the base. */
  zIndexClass?: string;
  /** When false, Escape does not dismiss this sheet. Use for a base sheet
   *  covered by an overlay so Esc dismisses only the topmost. Default true. */
  escDismisses?: boolean;
}

export const SideSheet: FC<SideSheetProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  widthClass = "w-full max-w-2xl",
  headerContent,
  headerClassName,
  hideCloseButton = false,
  closeLabel = "Close",
  zIndexClass = "z-50",
  escDismisses = true,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);

  useOverlay(isOpen, onClose, escDismisses);

  // Trap focus
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    lastActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Initial focus
    window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) {
        return;
      }
      const selector = [
        "button:not([disabled])",
        "a[href]",
        "textarea:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");
      const firstFocusable = panel.querySelector<HTMLElement>(selector);
      (firstFocusable ?? panel).focus();
    }, 0);

    return () => {
      lastActiveElementRef.current?.focus();
      lastActiveElementRef.current = null;
    };
  }, [isOpen]);

  // Focus cycle
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const getFocusable = () => {
      const selector = [
        "a[href]",
        "button:not([disabled])",
        "textarea:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");
      return Array.from(panel.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => {
          const style = window.getComputedStyle(el);
          return style.visibility !== "hidden" && style.display !== "none";
        },
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") {
        return;
      }
      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const [first] = focusable;
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === panel) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    panel.addEventListener("keydown", onKeyDown);
    return () => panel.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const drawerContent = (
    <div
      className={`fixed inset-0 ${zIndexClass} flex justify-end overflow-hidden bg-black/50 pr-2 backdrop-blur-sm sm:pr-3`}
      onClick={onClose}
    >
      <div
        id="drawer-content"
        className={`dark:bg-arti-background h-dvh max-h-dvh ${widthClass} animate-slideInPreview flex flex-col border-l border-slate-200 bg-white shadow-lg dark:border-slate-700`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby="drawer-content"
        ref={panelRef}
        tabIndex={-1}
      >
        <div
          className={
            headerClassName ??
            "flex flex-shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5 dark:border-slate-700 dark:bg-arti-background"
          }
        >
          {headerContent ?? (
            <div className="min-w-0">
              <h3 className="truncate font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
                {title}
              </h3>
              {subtitle && (
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  {subtitle}
                </div>
              )}
            </div>
          )}
          {!hideCloseButton && (
            <Button variant="secondary" size="sm" onClick={onClose}>
              {closeLabel}
            </Button>
          )}
        </div>

        <div className="custom-scrollbar flex-1 overflow-y-auto px-3 py-2.5 sm:px-4 sm:py-3">
          {children}
        </div>

        {footer && <OverlayFooter>{footer}</OverlayFooter>}
      </div>
    </div>
  );

  return createPortal(drawerContent, document.body);
};
